import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, Save, Megaphone, Trash2, Loader2, Palette, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { applyBrandColor } from '@/components/BrandProvider';
import { useI18n } from '@/components/I18nProvider';

export default function AdminSettingsPage() {
  const { t } = useI18n();
  const { settings, announcements, fetchSettings, updateSettings, fetchAnnouncements, createAnnouncement, deleteAnnouncement } = useAdminStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', content: '', type: 'info', target: 'all' });

  useEffect(() => {
    fetchSettings();
    fetchAnnouncements();
  }, [fetchSettings, fetchAnnouncements]);

  useEffect(() => {
    if (settings && !form) setForm(settings);
  }, [settings, form]);

  const handleSave = async () => {
    setSaving(true);
    const res = await updateSettings(form);
    setSaving(false);
    if (res.success) {
      toast.success(t('settingsSaved'));      if (form.brand_color) {
        applyBrandColor(form.brand_color);
      }
    }
    else toast.error(res.error || 'Hata');
  };

  const handleCreateAnn = async () => {
    if (!annForm.title || !annForm.content) return toast.error('Başlık ve içerik zorunlu');
    const res = await createAnnouncement(annForm);
    if (res.success) {
      toast.success('Duyuru oluşturuldu');
      setAnnForm({ title: '', content: '', type: 'info', target: 'all' });
    } else toast.error(res.error || 'Hata');
  };

  const handleDeleteAnn = async (id) => {
    const res = await deleteAnnouncement(id);
    if (res.success) toast.success('Duyuru silindi');
    else toast.error(res.error || 'Hata');
  };

  if (!form) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-settings-page">
        <h1 className="font-heading text-3xl font-bold">Sistem Ayarları</h1>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Sistem Ayarları</h1>
        <p className="mt-1 text-muted-foreground">Genel sistem yapılandırması ve duyurular</p>
      </div>

      {/* Brand Color Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5 text-primary" />Kurumsal Renk</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Uygulamanın ana rengini (primary) buradan değiştirebilirsiniz. Bu renk landing sayfası, butonlar ve tüm arayüzde uygulanır.</p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="space-y-1.5">
              <Label>Renk Kodu</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.brand_color || '#1a224f'}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  className="w-12 h-10 rounded-lg border cursor-pointer"
                  data-testid="brand-color-picker"
                />
                <Input
                  value={form.brand_color || '#1a224f'}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  placeholder="#1a224f"
                  className="h-10 w-36 font-mono"
                  data-testid="brand-color-input"
                />
                {form.brand_color && form.brand_color !== '#1a224f' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 text-xs gap-1.5"
                    onClick={() => setForm({ ...form, brand_color: '#1a224f' })}
                    data-testid="reset-color-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Sıfırla
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-1">
              <Label>Önizleme</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="h-10 w-10 rounded-lg" style={{ backgroundColor: form.brand_color || '#1a224f' }} />
                <div className="h-10 px-4 rounded-lg flex items-center text-white text-sm font-medium" style={{ backgroundColor: form.brand_color || '#1a224f' }}>
                  Örnek Buton
                </div>
                <div className="h-10 px-4 rounded-lg flex items-center text-sm font-medium border-2" style={{ borderColor: form.brand_color || '#1a224f', color: form.brand_color || '#1a224f' }}>
                  Outline
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />Varsayılan Limitler</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { key: 'default_daily_message_limit', label: 'Günlük Mesaj' },
              { key: 'default_monthly_message_limit', label: 'Aylık Mesaj' },
              { key: 'default_personnel_limit', label: t('rolePersonnel') },
              { key: 'default_cari_limit', label: 'Cari' },
              { key: 'default_payment_limit', label: 'Ödeme' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <Label>{label}</Label>
                <Input
                  type="number"
                  value={form[key] || 0}
                  onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value) || 0 })}
                  data-testid={`setting-${key}`}
                />
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Bakım Modu</Label>
              <p className="text-sm text-muted-foreground">Aktif edilirse yeni kayıtlar devre dışı kalır</p>
            </div>
            <Switch
              checked={form.maintenance_mode}
              onCheckedChange={(v) => setForm({ ...form, maintenance_mode: v })}
              data-testid="maintenance-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Yeni Kayıt</Label>
              <p className="text-sm text-muted-foreground">Yeni firma kayıtlarına izin ver</p>
            </div>
            <Switch
              checked={form.allow_registration}
              onCheckedChange={(v) => setForm({ ...form, allow_registration: v })}
              data-testid="registration-switch"
            />
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* Announcements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" />Duyurular</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="ann-title" />
            </div>
            <div className="space-y-2">
              <Label>Hedef</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={annForm.target} onChange={(e) => setAnnForm({ ...annForm, target: e.target.value })} data-testid="ann-target">
                <option value="all">Herkes</option>
                <option value="company_admin">Firma Yöneticileri</option>
                <option value="personnel">Personeller</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Icerik</Label>
            <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={annForm.content} onChange={(e) => setAnnForm({ ...annForm, content: e.target.value })} data-testid="ann-content" />
          </div>
          <Button onClick={handleCreateAnn} data-testid="create-ann-btn">Duyuru Oluştur</Button>

          <Separator />

          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="flex items-start justify-between rounded-lg border p-4" data-testid={`ann-${a.id}`}>
                <div>
                  <h4 className="font-medium">{a.title}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{a.content}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Hedef: {a.target} | {new Date(a.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDeleteAnn(a.id)} data-testid={`delete-ann-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {announcements.length === 0 && <p className="text-center text-sm text-muted-foreground">Henüz duyuru yok</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
